import React, { useEffect, useState, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-quartz.css';
import { Header } from '@backstage/core-components';
import 'ag-grid-enterprise';
import Loader from '../loader/Loader';
import { discoveryApiRef, useApi, fetchApiRef, identityApiRef } from '@backstage/core-plugin-api';
import '../NoteCustomstyle.css';

// Material UI dropdown components
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';

const EC2SavingsPlans = () => {
  const gridRef = useRef<any>(null);
  const containerStyle = useMemo(() => ({ width: "100%", height: "100vh" }), []);
  const gridStyle = useMemo(() => ({ height: 'calc(-200px + 80vh)', width: "100%", marginTop: '50px' }), []);
  const [rowData, setRowdata] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [org, setOrg] = useState('');
  const [tenantName, settenantName] = useState<string>('');  // tenant filter
  const [role, setRole] = useState<string>('');          // role (SuperAdmin, etc.)
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [tenantsFromDb, setTenantsFromDb] = useState<any[]>([]);
  const identityApi = useApi(identityApiRef)

  const fetchTenantsFromDb = async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('user');
      const response = await fetchApi.fetch(`${baseUrl}/tenants`);
      if (!response.ok) throw new Error("Failed to fetch tenants");
      return await response.json();
    } catch (e) {
      console.error("Error fetching tenants:", e);
      return [];
    }
  };

  const fetchTenant = async () => {
    try {
      const details = await identityApi.getBackstageIdentity();
      const id = details.ownershipEntityRefs.find(ref =>
        ref.startsWith('tenantId:')
      )?.split(':')[1] || null;

      console.log("ID ---------", id)
      return id
    } catch (err) {
      console.error('Failed to get tenantId:', err);
    }
  }; 

  useEffect(() => {
    const fetchData = async () => {
      try {
        const tenants = await fetchTenantsFromDb();
        const id = await fetchTenant()
        console.log(`orgName:${id}`)
        const tenantOrgName = localStorage.getItem(`orgName:${id}`);
        console.log('tenantOrgName------',tenantOrgName)
        if (tenantOrgName) setOrg(tenantOrgName);
        setTenantsFromDb(tenants);
        const baseurl = await discoveryApi.getBaseUrl('user');
        let data: any[] = [];
        try {
          const fetchresponse = await fetchApi.fetch(`${baseurl}/savings-plans-recommendation`, {
            method: 'POST',
          });
          if (!fetchresponse.ok) {
            const errorText = await fetchresponse.text();
            throw new Error(`API error: ${fetchresponse.status} - ${errorText}`);
          }
          let response = await fetchresponse.json();
          setRole(response.role || "");   //  backend should return role (same as in KMS)
          data = response.data || response; // adjust depending on backend response
          console.log('Fetched data:', data);
        } catch (error) {
          console.error('Fetch or JSON parse error:', error);
        }
        setRowdata(data);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    fetchData();

    const orgName = localStorage.getItem('orgName');
    if (orgName) {
      setOrg(orgName);
    }
  }, []);

  const columnDefs = useMemo(() => {
    const baseCols = [
    { headerName: "Tenant Name", field: "tenantName", headerTooltip: "Tenant Name" }, 
    { headerName: "Account Scope", field: "accountScope", headerTooltip: "Account Scope" },
    { headerName: "Account Id", field: "accountId", headerTooltip: "Account Id" },
    { headerName: "Savings Plans Type", field: "savingsPlansType", headerTooltip: "Savings Plans Type" },
    { headerName: "Term In Years", field: "termInYears", headerTooltip: "Term In Years" },
    { headerName: "Payment Option", field: "paymentOption", headerTooltip: "Payment Option" },
    { headerName: "Potential Cost Saving ($) per month", field: "estimatedMonthlySavingsAmount" },
    { headerName: "Potential Cost Saving ($) per year", field: "estimatedYearlySavingsAmount" },
    { headerName: "Potential Cost Saving ($) 3 years", field: "estimatedThreeYearSavingsAmount" },
  ];
  
  return baseCols.map(col =>
    col.field === "tenantName"
      ? { ...col, hide: role != "SuperAdmin", rowGroup: role != "SuperAdmin" ? false : col.rowGroup }
      : col
  );
}, [role]);

  const defaultColDef = useMemo(() => {
    return {
      flex: 1, filter: true, sortable: true, resizable: true, tooltipShowDelay: 0, tooltipInteraction: true, columnMenu: "new"
    };
  }, []);

  const gridOptions = { suppressContextMenu: true };

  //  filter rowData based on tenant dropdown
  const filteredData = tenantName
    ? rowData.filter(row => String(row.tenantName) === String(tenantName))
    : rowData;

  //  extract unique tenants
  const tenants = [...new Set(rowData.map(r => r.tenantName))];
  console.log("Tenants in dropdown:", tenants);

  return (
    <div style={containerStyle}>
      <div>
        <Header title="EC2 Savings Plans" subtitle={`${org}`} />
      </div>

      {/* Tenant Dropdown - only for SuperAdmin */}
      {role === "SuperAdmin" && (
        <div style={{ display: "flex", justifyContent: "flex-end", margin: "10px 0" }}>
          <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
            <InputLabel></InputLabel>
            <Select
              value={tenantName}
              onChange={(e) => settenantName(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  sx: {
                    // force vertical stacking
                    '& .MuiList-root': {
                      display: 'flex !important',
                      flexDirection: 'column !important',
                    },
                    // force left-aligned text inside items
                    '& .MuiMenuItem-root': {
                      justifyContent: 'flex-start',
                      pl: 2,
                    },
                  },
                },
              }}
            >
              <MenuItem value="">All Tenants</MenuItem>
              {tenantsFromDb.map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.name}>
                  {tenant.name}
                </MenuItem>
                ))}
            </Select>
          </FormControl>
        </div>
      )}

      {loading ? (<Loader />) : (
        <div>
          <div className="ag-theme-quartz resourcecost" style={{ ...gridStyle, marginTop: 10 }}>
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              gridOptions={gridOptions}
            />
          </div>
          <div className="recommended-action-container">
            <div style={{ overflowY: 'scroll', width: '98.6%' }}>
              <strong>Recommended Action</strong>
              <ul>
                <li>
                  <strong>Enable EC2 Savings Plans in AWS</strong>
                  <p>EC2 Savings Plans offer flexible pricing options that help reduce your EC2 costs by committing to consistent usage over a 1- or 3-year term.</p>
                  <ul>
                    <li>Select the plan type (e.g., Compute or Instance Savings Plans).</li>
                    <li>Set your desired term length and payment option (All upfront, Partial upfront, or No upfront).</li>
                  </ul>
                  <p>Considerations:</p>
                  <ul>
                    <li>Ensure your expected EC2 usage aligns with the selected Savings Plan to maximize savings.</li>
                    <li>Savings Plans may not always result in cost savings if your actual usage deviates significantly from your committed usage.</li>
                  </ul>
                </li>
              </ul>
            </div>
          </div>
          <div className="note-container">
            <p>
              <strong>Note*</strong><span className="note-text">: The cost savings estimate compares monthly On-Demand spend with projected costs under recommended Savings Plans. It analyzes past usage and existing commitments to determine current On-Demand spending. The projected monthly spend includes new Savings Plans and any remaining On-Demand usage. Estimated savings are calculated by subtracting projected costs from current On-Demand spend. This provides insight into potential savings from adopting recommended Savings Plans.</span>
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default EC2SavingsPlans;